import { useState, useEffect } from 'react';
import { ReactNode } from 'react';
import { CountryConstant } from '../constants/CountryConstants';
import { ProvinceConstant } from '../constants/ProvinceConstants';
import { useProvinces } from '../hooks/useProvinceApi';

export interface DropdownOption {
  value: string;
  label: ReactNode;
}

interface ProvinceLogicProps {
  countryId?: string;
  provinceId?: string;
  provinceOther?: string;
  isExistingMember?: boolean;
  onProvinceChange?: (provinceId: string | undefined, provinceOther?: string | undefined) => void;
  autoSetNotOnList?: boolean;
}

interface ProvinceLogicReturn {
  provinceOptions: DropdownOption[];
  saProvinceOptions: DropdownOption[];
  isLoadingProvinces: boolean;
  shouldShowProvinceOther: boolean;
  findProvinceByName: (provinceName: string) => string | null;
  setProvinceOptionsToNotOnList: () => void;
}

export const provinceLogic = ({
  countryId,
  provinceId,
  provinceOther,
  isExistingMember = false,
  onProvinceChange,
  autoSetNotOnList = false,
}: ProvinceLogicProps): ProvinceLogicReturn => {
  const [provinceOptions, setProvinceOptions] = useState<DropdownOption[]>([]);
  
  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();

  // Map provinces to dropdown format
  const saProvinceOptions: DropdownOption[] = (provinces || []).map((p) => ({
    value: p.id ?? '',
    label: p.title ?? '',
  }));

  // Handle province options based on country and existing data
  useEffect(() => {
    const notOnListOnly = [
      { value: ProvinceConstant.NOT_ON_LIST, label: 'Not on List' },
    ];

    // Handle existing member with provinceOther but no provinceId
    if (isExistingMember && !provinceId && provinceOther) {
      setProvinceOptions(notOnListOnly);
      return;
    }

    // Handle non-South African countries
    if (countryId && countryId !== CountryConstant.SOUTH_AFRICA) {
      setProvinceOptions(notOnListOnly);
      return;
    }

    // Handle South African provinces
    if (countryId === CountryConstant.SOUTH_AFRICA || !countryId) {
      // Only update if saProvinceOptions is loaded
      if (saProvinceOptions.length > 0) {
        // Check if current provinceId is "Not on List"
        if (provinceId === ProvinceConstant.NOT_ON_LIST) {
          setProvinceOptions(notOnListOnly);
        } else {
          setProvinceOptions(saProvinceOptions);
        }
      }
      return;
    }
  }, [countryId, provinceId, provinceOther, isExistingMember, saProvinceOptions.length]);

  // Separate effect to handle auto-setting province when country changes
  useEffect(() => {
    if (!autoSetNotOnList || !onProvinceChange) return;

    // Auto-set to "Not on List" when non-SA country is selected
    if (countryId && countryId !== CountryConstant.SOUTH_AFRICA && provinceId !== ProvinceConstant.NOT_ON_LIST) {
      onProvinceChange(ProvinceConstant.NOT_ON_LIST, provinceOther);
      return;
    }

    // Clear "Not on List" when switching back to SA
    if ((countryId === CountryConstant.SOUTH_AFRICA || !countryId) && provinceId === ProvinceConstant.NOT_ON_LIST) {
      onProvinceChange(undefined, undefined);
      return;
    }
  }, [countryId, autoSetNotOnList]); // Only depend on countryId changes

  const shouldShowProvinceOther = 
    (countryId !== CountryConstant.SOUTH_AFRICA && !!countryId) || 
    provinceId === ProvinceConstant.NOT_ON_LIST;

  const findProvinceByName = (provinceName: string): string | null => {
    if (!provinceName || saProvinceOptions.length === 0) return null;

    const matchedProvince = saProvinceOptions.find(
      (province) =>
        String(province.label)
          .toLowerCase()
          .includes(provinceName.toLowerCase()) ||
        provinceName
          .toLowerCase()
          .includes(String(province.label).toLowerCase())
    );

    return matchedProvince ? matchedProvince.value : null;
  };

  const setProvinceOptionsToNotOnList = () => {
    const notOnListOnly = [
      { value: ProvinceConstant.NOT_ON_LIST, label: 'Not on List' },
    ];
    setProvinceOptions(notOnListOnly);
  };

  return {
    provinceOptions,
    saProvinceOptions,
    isLoadingProvinces,
    shouldShowProvinceOther,
    findProvinceByName,
    setProvinceOptionsToNotOnList,
  };
};